// F10 配車リアルタイム: ドライバー GPS の距離ベース送信。
//
// 方針 (決定済み): 時間間隔ではなく**距離ベース**で間引く。前回送信地点から
// 500m を超えて移動したときだけ送信する。停車中は送らないため LIFF (LINE 内ブラウザ)
// のバッテリ消費を抑えられる。

/** 2 点間の距離 (メートル)。Haversine 公式。 */
export function haversineMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const R = 6_371_000; // 地球半径 (m)
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

export interface Coord {
  lat: number;
  lng: number;
}

/**
 * 距離ゲート。前回送信地点から `thresholdM` を超えたときだけ `true` を返し、
 * 同時に基準点を更新する。初回 (基準点なし) は必ず `true`。
 */
export function createDistanceGate(thresholdM = 500): (next: Coord) => boolean {
  let last: Coord | null = null;
  return (next: Coord): boolean => {
    if (last && haversineMeters(last, next) <= thresholdM) {
      return false;
    }
    last = next;
    return true;
  };
}

/** navigator.geolocation 最小インターフェース (テスト注入用)。 */
export interface GeolocationLike {
  watchPosition(
    success: (
      pos: { coords: GeolocationCoordinatesLike; timestamp: number } | GeolocationPositionLike,
    ) => void,
    error?: (err: unknown) => void,
    options?: { enableHighAccuracy?: boolean; maximumAge?: number; timeout?: number },
  ): number;
  clearWatch(id: number): void;
}

interface GeolocationCoordinatesLike {
  latitude: number;
  longitude: number;
  heading?: number | null;
  speed?: number | null;
  accuracy?: number | null;
}

interface GeolocationPositionLike {
  coords: GeolocationCoordinatesLike;
  timestamp: number;
}

export interface LocationReporterOptions {
  /** GPS 1 点を受け取り API へ送る関数 (reportLocationDriver をバインドして渡す)。 */
  send: (input: {
    latitude: number;
    longitude: number;
    heading?: number;
    speed_kmh?: number;
    accuracy_m?: number;
    recorded_at: string;
  }) => Promise<unknown>;
  /** 送信判定の距離しきい値 (m)。既定 500。 */
  thresholdM?: number;
  /** 注入用 (既定 navigator.geolocation)。 */
  geolocation?: GeolocationLike;
  /** 注入用 (既定 (ms)=>ISO 文字列)。 */
  toIso?: (ms: number) => string;
  /** 送信失敗時のハンドラ (既定 no-op = best-effort)。 */
  onError?: (e: unknown) => void;
}

/**
 * GPS を監視し、距離ゲートを通過したときだけ送信を始める。
 * 返り値の `stop()` で監視を解除する。geolocation 非対応環境では no-op を返す。
 */
export function startLocationReporting(opts: LocationReporterOptions): { stop: () => void } {
  const geo =
    opts.geolocation ??
    (typeof navigator !== "undefined"
      ? (navigator.geolocation as unknown as GeolocationLike)
      : undefined);
  if (!geo) return { stop: () => {} };

  const gate = createDistanceGate(opts.thresholdM ?? 500);
  const toIso = opts.toIso ?? ((ms: number) => new Date(ms).toISOString());
  const onError = opts.onError ?? (() => {});

  const watchId = geo.watchPosition(
    (pos) => {
      const p = pos as GeolocationPositionLike;
      const c = p.coords;
      if (!gate({ lat: c.latitude, lng: c.longitude })) return;
      void Promise.resolve(
        opts.send({
          latitude: c.latitude,
          longitude: c.longitude,
          ...(c.heading != null ? { heading: c.heading } : {}),
          ...(c.speed != null ? { speed_kmh: c.speed * 3.6 } : {}), // m/s → km/h
          ...(c.accuracy != null ? { accuracy_m: c.accuracy } : {}),
          recorded_at: toIso(p.timestamp),
        }),
      ).catch(onError);
    },
    onError,
    { enableHighAccuracy: true, maximumAge: 0 },
  );

  return { stop: () => geo.clearWatch(watchId) };
}
